import { Navigate, Route, Routes } from 'react-router-dom';
import { AdminShell } from './components/AdminShell';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PublicShell } from './components/PublicShell';
import { RouteScrollReset } from './components/RouteScrollReset';
import { WorkspaceShell } from './components/WorkspaceShell';
import { RepresentativeShell } from './components/RepresentativeShell';
import { AdBuilderPage } from './pages/AdBuilderPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdvertisePage } from './pages/AdvertisePage';
import { AgencyDashboardPage } from './pages/AgencyDashboardPage';
import { AgencyHostelsPage } from './pages/AgencyHostelsPage';
import { AgencyOnboardingPage } from './pages/AgencyOnboardingPage';
import { AgencyProfilePage } from './pages/AgencyProfilePage';
import { AgencyRequestsPage } from './pages/AgencyRequestsPage';
import { AgencySettingsPage } from './pages/AgencySettingsPage';
import { AgencyVerificationPage } from './pages/AgencyVerificationPage';
import { AuthCompletePage } from './pages/auth/AuthCompletePage';
import { LoginPage } from './pages/auth/LoginPage';
import { CampaignDetailPage } from './pages/CampaignDetailPage';
import { ExplorePage } from './pages/ExplorePage';
import { HelpPage } from './pages/HelpPage';
import { HomePage } from './pages/HomePage';
import { HostelDetailPage } from './pages/HostelDetailPage';
import { MessagesPage } from './pages/MessagesPage';
import { MyCampaignsPage } from './pages/MyCampaignsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { RequestDetailPage } from './pages/RequestDetailPage';
import { RequestPage } from './pages/RequestPage';
import { RequestsPage } from './pages/RequestsPage';
import { ReviewPage } from './pages/ReviewPage';
import { SavedPage } from './pages/SavedPage';
import { RepresentativeAccessPage } from './pages/RepresentativeAccessPage';
import { RepresentativeApplyPage } from './pages/RepresentativeApplyPage';
import { RepresentativeDashboardPage } from './pages/RepresentativeDashboardPage';
import { SettingsPage } from './pages/SettingsPage';
import { ContactPage, PrivacyPage, TermsPage } from './pages/LegalPage';

const protect = (element: React.ReactNode, roles?: Array<'student'|'agency'|'advertiser'|'admin'>) => (
  <ProtectedRoute roles={roles}>{element}</ProtectedRoute>
);

export default function App() {
  return (
    <>
      <RouteScrollReset />
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/complete" element={<AuthCompletePage />} />

      {/* Public Marketplace Shell */}
      <Route element={<PublicShell />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/hostels/:id" element={<HostelDetailPage />} />
        <Route path="/agencies/:id" element={<AgencyProfilePage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/requests/new" element={protect(<RequestPage />)} />
        <Route path="/requests" element={protect(<RequestsPage />)} />
        <Route path="/requests/:id" element={protect(<RequestDetailPage />)} />
        <Route path="/messages" element={protect(<MessagesPage />)} />
        <Route path="/messages/:id" element={protect(<MessagesPage />)} />
        <Route path="/saved" element={protect(<SavedPage />)} />
        <Route path="/notifications" element={protect(<NotificationsPage />)} />
        <Route path="/reviews/new" element={protect(<ReviewPage />)} />
        <Route path="/settings" element={protect(<SettingsPage />)} />
        <Route path="/agency/onboarding" element={protect(<AgencyOnboardingPage />)} />
        <Route path="/advertise" element={<AdvertisePage />} />
        <Route path="/advertise/create" element={protect(<AdBuilderPage />)} />
        <Route path="/advertise/campaigns" element={protect(<MyCampaignsPage />)} />
        <Route path="/advertise/campaigns/:id" element={protect(<CampaignDetailPage />)} />
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route element={<RepresentativeShell />}>
        <Route path="/representative/access" element={<RepresentativeAccessPage />} />
        <Route path="/representative/apply" element={protect(<RepresentativeApplyPage />)} />
        <Route path="/representative" element={protect(<RepresentativeDashboardPage />)} />
      </Route>

      {/* Agency Workspace Shell */}
      <Route element={<WorkspaceShell />}>
        <Route path="/agency" element={protect(<AgencyDashboardPage />, ['agency', 'admin'])} />
        <Route path="/agency/hostels" element={protect(<AgencyHostelsPage />, ['agency', 'admin'])} />
        <Route path="/agency/requests" element={protect(<AgencyRequestsPage />, ['agency', 'admin'])} />
        <Route path="/agency/messages" element={protect(<MessagesPage />, ['agency', 'admin'])} />
        <Route path="/agency/messages/:id" element={protect(<MessagesPage />, ['agency', 'admin'])} />
        <Route path="/agency/verification" element={protect(<AgencyVerificationPage />, ['agency', 'admin'])} />
        <Route path="/agency/settings" element={protect(<AgencySettingsPage />, ['agency', 'admin'])} />
      </Route>

      {/* Admin Moderation Shell */}
      <Route element={<AdminShell />}>
        <Route path="/admin" element={protect(<AdminDashboardPage />, ['admin'])} />
      </Route>
      </Routes>
    </>
  );
}
